import { useState, useEffect, useCallback } from 'react';

const useAnalytics = () => {
    // **STATE MANAGEMENT**
    const [analytics, setAnalytics] = useState(null);
    const [advancedAnalytics, setAdvancedAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // **FILTER STATE**
    const [dateRange, setDateRange] = useState('180'); // 6 months default
    const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
    const [tempCustomDateRange, setTempCustomDateRange] = useState({ start: '', end: '' }); // Geçici tarih state'i
    const [showCustomDate, setShowCustomDate] = useState(false);

    // **PROMISSORY NOTE LIST STATE**
    const [promissoryNoteListLoading, setPromissoryNoteListLoading] = useState(false);
    const [promissoryNoteList, setPromissoryNoteList] = useState([]);

    // **MODAL STATE**
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [modalType, setModalType] = useState('');
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [detailedModalData, setDetailedModalData] = useState(null);

    // **HELPER: Build Filter Options**
    const buildFilterOptions = useCallback(() => {
        let filters = {};
        
        if (dateRange === 'custom') {
            // Özel tarih seçiliyse ve tarihler doluysa kullan
            if (customDateRange.start && customDateRange.end) {
                filters = {
                    startDate: customDateRange.start,
                    endDate: customDateRange.end
                };
            } else {
                // Tarihler henüz girilmemişse default 180 gün kullan
                filters = { days: 180 };
            }
        } else {
            filters = { days: parseInt(dateRange) };
        }
        return filters;
    }, [dateRange, customDateRange]);

    // **API CALLS**
    const fetchMonthlyFinancialAnalytics = async (filters) => {
        const response = await fetch(`/api/analytics/monthly-financial?${new URLSearchParams(filters)}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });
        return response.json();
    };

    const fetchMonthlyQuantityAnalytics = async (filters) => {
        const response = await fetch(`/api/analytics/monthly-quantity?${new URLSearchParams(filters)}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });
        return response.json();
    };

    const fetchMonthlySalesTypesAnalytics = async (filters) => {
        const response = await fetch(`/api/analytics/monthly-sales-types?${new URLSearchParams(filters)}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });
        return response.json();
    };

    const fetchAdvancedAnalytics = async (filters) => {
        const queryParams = new URLSearchParams(filters);
        const response = await fetch(`/api/analytics/advanced?${queryParams}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });
        return response.json();
    };

    const fetchModalData = async (type, filters) => {
        const queryParams = new URLSearchParams({
            type: type,
            ...filters
        });
        const response = await fetch(`/api/analytics/modal-data?${queryParams}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });
        return response.json();
    };

    // Senetli araç listesi fetch fonksiyonu
    const fetchPromissoryNoteList = async (filters) => {
        const queryParams = new URLSearchParams(filters);
        const response = await fetch(`/api/analytics/promissory-note-list?${queryParams}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        });
        return response.json();
    };

    // **LOAD ANALYTICS DATA**
    const loadAnalyticsData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const filters = buildFilterOptions();

            const [financialData, quantityData, salesTypesData] = await Promise.all([
                fetchMonthlyFinancialAnalytics(filters),
                fetchMonthlyQuantityAnalytics(filters),
                fetchMonthlySalesTypesAnalytics(filters)
            ]);

     

            // Calculate KPIs from chart data
            const totalSalesRevenue = financialData.data.monthlyFinancial?.reduce((sum, item) => sum + (item.totalSalesRevenue || 0), 0) || 0;
            const totalPurchaseAmount = financialData.data.monthlyFinancial?.reduce((sum, item) => sum + (item.totalPurchaseCost || 0), 0) || 0;
            const totalSold = quantityData.data.monthlyQuantity?.reduce((sum, item) => sum + (item.soldCount || 0), 0) || 0;

            const combinedAnalytics = {
                success: true,
                data: {
                    // Chart data
                    purchaseVsSales: financialData.data.monthlyFinancial?.map(item => ({
                        month: item.monthName,
                        purchases: item.totalPurchaseCost,
                        sales: item.totalSalesRevenue
                    })) || [],

                    purchaseVsSalesCount: quantityData.data.monthlyQuantity?.map(item => ({
                        month: item.monthName,
                        purchaseCount: item.purchasedCount,
                        salesCount: item.soldCount
                    })) || [],

                    monthlySalesData: salesTypesData.data.monthlySalesData || [],

                    // Basic KPIs (fallback)
                    vehicles: {
                        totalSalesRevenue,
                        totalPurchaseAmount,
                        totalSold
                    }
                }
            };

            if (combinedAnalytics.success) {
                setAnalytics(combinedAnalytics.data);
            } else {
                setError('Analytics verisi alınamadı');
            }

        } catch (error) {
            console.error('Analytics fetch error:', error);
            setError('Analytics verileri yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    }, [buildFilterOptions]);

    // **LOAD ADVANCED ANALYTICS (KPIs)**
    const loadAdvancedAnalytics = useCallback(async () => {
        try {
            const filters = buildFilterOptions();
            const data = await fetchAdvancedAnalytics(filters);

            if (data.success) {
                setAdvancedAnalytics(data.data);
                console.log('Advanced analytics loaded:', data.data);
            } else {
                console.error('Advanced analytics fetch failed:', data.message);
            }
        } catch (error) {
            console.error('Advanced analytics fetch error:', error);
        }
    }, [buildFilterOptions]);

    // **OPEN MODAL WITH DATA FETCHING**
    const openModal = async (type, chartData) => {
        try {
            setModalType(type);
            setModalData(chartData);
            setDetailedModalData(null);
            setModalError(null);
            setShowModal(true);
            setModalLoading(true);

            console.log(`Opening modal: ${type}`);

            const modalFilters = buildFilterOptions();
            const detailedData = await fetchModalData(type, modalFilters);

            if (detailedData.success) {
                setDetailedModalData(detailedData.data);
                console.log(`Modal data loaded for ${type}:`, detailedData.data);
            } else {
                setModalError(detailedData.message || 'Veri yüklenemedi');
            }

        } catch (error) {
            console.error(`Modal data fetch error for ${type}:`, error);
            setModalError('Detaylı veriler yüklenirken hata oluştu');
        } finally {
            setModalLoading(false);
        }
    };

    // **EXPORT FUNCTIONS**
    const exportToExcel = async () => {
        try {
            const filters = buildFilterOptions();
            const response = await fetch('/api/analytics/export-excel', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ filters })
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `admin-analytics-${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                setSuccess('Excel dosyası indirildi');
            } else {
                setError('Excel export sırasında hata oluştu');
            }
        } catch (error) {
            console.error('Excel export error:', error);
            setError('Excel export sırasında hata oluştu');
        }
    };

    // **FILTER HANDLERS**
    const handleDateRangeChange = (newRange) => {
        setDateRange(newRange);
        setShowCustomDate(newRange === 'custom');
        
        // Özel tarih seçildiğinde mevcut custom date'i temp'e kopyala
        if (newRange === 'custom') {
            setTempCustomDateRange({ ...customDateRange });
        }
    };

    const handleCustomDateChange = (field, value) => {
        // Sadece temp state'i güncelle, henüz filtreleme yapma
        setTempCustomDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Özel tarih filtresini uygula
    const applyCustomDateFilter = () => {
        if (tempCustomDateRange.start && tempCustomDateRange.end) {
            setCustomDateRange({ ...tempCustomDateRange });
        }
    };

    // Senetli araç listesini yükle
    const loadPromissoryNoteList = async () => {
        try {
            setPromissoryNoteListLoading(true);
            const filters = buildFilterOptions();
            const data = await fetchPromissoryNoteList(filters);

            if (data.success) {
                setPromissoryNoteList(data.data || []);
            } else {
                console.error('Promissory note list fetch failed:', data.message);
                setPromissoryNoteList([]);
            }
        } catch (error) {
            console.error('Promissory note list fetch error:', error);
            setPromissoryNoteList([]);
        } finally {
            setPromissoryNoteListLoading(false);
        }
    };

    // **MESSAGE HANDLERS**
    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    const closeModal = () => {
        setShowModal(false);
        setModalData(null);
        setDetailedModalData(null);
        setModalError(null);
        setModalType('');
    };

    // **COMPUTED VALUES**
    const kpiData = {
        totalRevenue: advancedAnalytics?.kpis?.totalRevenue || 0,
        totalProfit: advancedAnalytics?.kpis?.totalProfit || 0,
        totalVehiclesSold: advancedAnalytics?.kpis?.totalVehiclesSold || 0,
        profitMargin: advancedAnalytics?.kpis?.profitMargin || 0,
        totalPurchaseAmount: advancedAnalytics?.kpis?.totalPurchaseAmount || 0,
        averageDaysToSell: Math.round(advancedAnalytics?.averageDaysToSell || 0),
        // Senetli Satış KPI'ları
        promissoryNoteCount: advancedAnalytics?.kpis?.promissoryNoteCount || 0,
        promissoryNotePrincipal: advancedAnalytics?.kpis?.promissoryNotePrincipal || 0,
        promissoryNoteTotalReturn: advancedAnalytics?.kpis?.promissoryNoteTotalReturn || 0
    };

    const chartData = {
        purchaseVsSales: analytics?.purchaseVsSales || [],
        purchaseVsSalesCount: analytics?.purchaseVsSalesCount || [],
        monthlySalesData: analytics?.monthlySalesData || []
    };

    // **EFFECTS**
    useEffect(() => {
        loadAnalyticsData();
        loadAdvancedAnalytics();
    }, [loadAnalyticsData, loadAdvancedAnalytics]);

    // Auto-clear success messages
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    // **RETURN HOOK INTERFACE**
    return {
        // State
        analytics,
        advancedAnalytics,
        loading,
        error,
        success,
        kpiData,
        chartData,

        // Filter State
        dateRange,
        customDateRange,
        tempCustomDateRange,
        showCustomDate,

        // Modal State
        showModal,
        modalData,
        modalType,
        modalLoading,
        modalError,
        detailedModalData,

        // Promissory Note List State
        promissoryNoteListLoading,
        promissoryNoteList,

        // Actions
        loadAnalyticsData,
        loadAdvancedAnalytics,
        openModal,
        closeModal,
        exportToExcel,
        handleDateRangeChange,
        handleCustomDateChange,
        applyCustomDateFilter,
        loadPromissoryNoteList,
        clearMessages,

        // Computed
        hasData: analytics !== null,
        isReady: !loading && analytics !== null
    };
};

export default useAnalytics;
